import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { FormattedMessage } from 'react-intl';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { Icon } from 'ui';
import { sortCOPSModelToParam, sortParamToCOPSModel } from 'utils/helpers';
import { sortTypes } from '../../utils/app-configs';

const StyledButton = styled.div`
  border-radius: 3px;
  width: 50px;
  height: 25px;
  border: 2px solid ${p => p.theme.colors.theme};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const OffButton = styled(StyledButton)`
  ${p => p.disabled && 'pointer-events: none;'}
  color: ${p => p.theme.colors.theme};
  background-color: #fff;
  user-select: none;
`;

const OnButton = styled(StyledButton)`
  ${p => p.disabled && 'pointer-events: none;'}
  background-color: ${p => p.theme.colors.theme};
  color: #fff;
  i {
    margin-left: 5px;
    color: #fff;
    cursor: pointer;
  }
`;

const getSortIcon = (icon) => {
  const iconClass = icon?.replace('<i class="', '')?.replace('"></i>', '');
  return <Icon className={iconClass} />;
};

const SortButton = ({
  id,
  icons = {},
  activeSort,
  defaultSorting,
  updateCb,
  disabled = false,
  isLocal = false,
}) => {
  const [sortOption, setSortOption] = useState();
  const [sortOptions, setSortOptions] = useState([]);
  const dispatch = useDispatch();

  useEffect(() => {
    setSortOptions(sortParamToCOPSModel(activeSort || (!isLocal ? defaultSorting : '')));
  }, [activeSort]);

  useDeepCompareEffect(() => {
    setSortOption(sortOptions?.find(o => o.sortColumn?.toLowerCase() === id?.toLowerCase()));
  }, [sortOptions, id]);

  const handleSort = () => {
    let options = sortOptions;
    if (!sortOption) {
      options = [
        ...sortOptions,
        {
          sortColumn: id,
          sortIndex: sortOptions.length,
          sortDirection: sortTypes.ascending,
        },
      ];
    } else if (sortOption.sortDirection === sortTypes.ascending) {
      options = sortOptions?.map((o) => {
        if (o.sortColumn?.toLowerCase() === id?.toLowerCase()) {
          o.sortDirection = sortTypes.descending;
        }
        return o;
      });
    } else if (sortOption.sortDirection === sortTypes.manual) {
      options = sortOptions?.map((o) => {
        if (o.sortColumn?.toLowerCase() === id?.toLowerCase()) {
          o.sortDirection = sortTypes.ascending;
        }
        return o;
      });
    } else if (!isLocal && sortOptions.length === 1) {
      options = sortOptions?.map((o) => {
        if (o.sortColumn?.toLowerCase() === id?.toLowerCase()) {
          o.sortDirection = sortTypes.ascending;
        }
        return o;
      });
    } else {
      options = sortOptions
        ?.filter(o => o.sortColumn?.toLowerCase() !== id?.toLowerCase())
        ?.sort((a, b) => a.sortIndex - b.sortIndex)
        ?.map((o) => {
          if (o.sortIndex > sortOption.sortIndex) {
            o.sortIndex -= 1;
          }
          return o;
        });
    }
    if (!isLocal) {
      dispatch(updateCb(sortCOPSModelToParam(options)));
    } else {
      updateCb(sortCOPSModelToParam(options));
    }
  };

  if (!sortOption || sortOption?.sortDirection === sortTypes.manual) {
    return (
      <OffButton disabled={disabled} onClick={handleSort}>
        <FormattedMessage id='worklist.filters.OFF' />
      </OffButton>
    );
  }
  return <OnButton disabled={disabled} onClick={handleSort}>
    {sortOption.sortIndex + 1}
    {getSortIcon(sortOption.sortDirection === sortTypes.ascending ?
      icons.sortAscending :
      icons.sortDescending,
    )}
  </OnButton>;
};

SortButton.propTypes = {
  id: PropTypes.string,
  icons: PropTypes.object,
  activeSort: PropTypes.string,
  updateCb: PropTypes.func,
  defaultSorting: PropTypes.string,
  disabled: PropTypes.bool,
  isLocal: PropTypes.bool,
};

export default SortButton;
